import React, { forwardRef, useImperativeHandle, useRef, useCallback, useState, memo, useEffect, useMemo } from 'react';
import { View, Modal, Pressable, Platform, Alert } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import BaseHierarchy, { BaseHierarchyHandle, HierarchyPosition, HierarchyLink } from '../../../../../../components/Hierarchy/BaseHierarchy';
import DraggableActionCard from '../../../../../../components/Hierarchy/DraggableActionCard';
import ActionLinkHandler, { ActionLinkHandlerHandle } from './ActionLinkHandler';
import { debounce } from 'lodash';
import { MotiView } from 'moti';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import actionList from './utils/actionList';
import { ADD_ACTION } from '@reduxLocal/actions/types';
import apis from '@api/api';
import ActionEditorSlidePanel from './ActionEditorSlidePanel';
import { getNodeType } from './utils/workflowNodeTypes';

interface UserHierarchyProps {
  schemaData: any;
  openAddUserModal?: () => void;
  openAddActionModal?: () => void;
  handleSnap: (value: any) => void;
  ref: any;
  isLoading: boolean;
  dataId?: string;
}

interface UserHierarchyHandle {
  zoomDisabled: () => void;
  zoomEnabled: () => void;
  appendPosition: (values: any, i: number) => void;
  moveTo: (x: number, y: number) => void;
  zoomBy: (zoom: number) => void;
  centerView: () => void;
  centerOnPrimary: () => void;
  centerOnItem: (itemId: string) => void;
}

export type CombinedHandle = UserHierarchyHandle & {
  zoomableViewMethods: any;
};

export { CombinedHandle as ActionHierarchyHandle };

const ActionHierarchy = forwardRef<CombinedHandle, UserHierarchyProps>((props, ref) => {
  const { schemaData, openAddUserModal, openAddActionModal, isLoading, handleSnap, dataId } = props;

  
  // Get actions from Redux store
  const actions = useSelector((state: DefaultRootState) => {    
    return state.actionMaker.present.actions;
  });


  const baseHierarchyRef = useRef<BaseHierarchyHandle>(null);

  // Action positions ref for direct access (following UserLinkHandler pattern)
  const actionPositionsRef = useRef<Record<string, HierarchyPosition>>({});

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedActionType, setSelectedActionType] = useState<'Create' | 'Read' | 'Update' | 'Delete'>('Create');
  const [activeTab, setActiveTab] = useState<'type' | 'config' | 'conditions' | 'workflow' | 'workflowNodes' | 'history' | 'agentic'>('type');
  const [selectedActionKey, setSelectedActionKey] = useState<string>('email');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [conditions, setConditions] = useState<Array<{
    id: string;
    field: string;
    operator: string;
    value: string;
    action: string; // What to do if condition is met
  }>>([]);
  const theme = themeSelector();
  const dispatch = useDispatch();
  const formRef = useRef<any>(null);

  // Workflow node state - only store node metadata, not positions
  // Positions are managed by BaseHierarchy via DraggableActionCard's useSharedValue
  const [workflowNodes, setWorkflowNodes] = useState<Array<{ id: string; type: string; name: string; position: { x: number; y: number }; config?: any }>>([]);
  const [workflowConnections, setWorkflowConnections] = useState<Array<{ source: { nodeId: string; outputIndex: number }; target: { nodeId: string; inputIndex: number }; type?: string }>>([]);

  // Workflow loading state
  const [currentWorkflowId, setCurrentWorkflowId] = useState<string | null>(null);
  const [workflowData, setWorkflowData] = useState<any>(null);
  const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(false);

  // Current action being edited (for workflow linking)
  const [editingAction, setEditingAction] = useState<any>(null);

  // Load workflow from API
  const loadWorkflow = useCallback(async (workflowId: string) => {
    setIsLoadingWorkflow(true);
    try {
      console.log('📥 Loading workflow:', workflowId);
      const response = await apis.getWorkflow(workflowId);
      const workflow = response.workflow || response;

      console.log('✅ Workflow loaded:', workflow);

      // Convert workflow nodes to local format
      const nodes = workflow.nodes || [];
      const connections = workflow.connections || [];

      setWorkflowNodes(nodes);
      setWorkflowConnections(connections);
      setCurrentWorkflowId(workflowId);
      setWorkflowData(workflow);

      console.log('✅ Workflow nodes and connections loaded:', {
        nodesCount: nodes.length,
        connectionsCount: connections.length
      });
    } catch (error) {
      console.error('❌ Failed to load workflow:', error);
      Alert.alert('Error', `Failed to load workflow: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoadingWorkflow(false);
    }
  }, []);

  // Handle workflow node movement
  const handleWorkflowNodeMove = useCallback((nodeId: string, position: { x: number; y: number }) => {
    setWorkflowNodes(prev => prev.map(node =>
      node.id === nodeId ? { ...node, position } : node
    ));
  }, []);

  // Handle workflow node addition
  const handleWorkflowNodeAdd = useCallback((node: { type: string; name: string; category: string; position: { x: number; y: number }; config?: any }) => {
    const newNode = {
      id: `node-${Date.now()}`,
      type: node.type,
      name: node.name,
      position: node.position, // Initial position only - BaseHierarchy will manage it after
      config: node.config || {}
    };
    setWorkflowNodes(prev => {
      const updated = [...prev, newNode];
      console.log('✅ Workflow node added. Total nodes:', updated.length);
      return updated;
    });
    // Don't close modal - allow adding multiple nodes
    Alert.alert('Success', `${node.name} node added to workflow!`);
  }, []);

  // Save workflow function - defined after actionItems
  const saveWorkflow = useCallback(async () => {
    try {
      console.log('💾 Saving workflow...', {
        workflowId: currentWorkflowId,
        nodesCount: workflowNodes.length,
        connectionsCount: workflowConnections.length
      });

      // Get current actionItems from the component state
      // We'll use actions from Redux and workflowNodes instead
      const workflowActionNodes = actions
        .filter(action => action.id === editingAction?.id)
        .map(action => ({
          id: action.id,
          type: action.key || action.type || 'action',
          name: action.title || 'Action',
          position: action.position || { x: 0, y: 0 },
          config: action.config || {}
        }));

      const workflowDataToSave = {
        name: editingAction?.title || schemaData?.title || 'Untitled Workflow',
        description: editingAction?.description || schemaData?.description || '',
        nodes: [...workflowNodes, ...workflowActionNodes],
        connections: workflowConnections,
        active: workflowData?.active || false,
        version: (workflowData?.version || 0) + 1
      };

      let savedWorkflowId = currentWorkflowId;

      if (currentWorkflowId) {
        // Update existing workflow
        await apis.updateWorkflow(currentWorkflowId, workflowDataToSave);
        console.log('✅ Workflow updated:', currentWorkflowId);
      } else {
        // Create new workflow
        const response = await apis.createWorkflow(workflowDataToSave);
        savedWorkflowId = response.workflow?._id || response._id || response.id;
        console.log('✅ Workflow created:', savedWorkflowId);

        // Link workflow to action
        if (editingAction) {
          setCurrentWorkflowId(savedWorkflowId);
          // Update action with workflowId
          const updatedAction = {
            ...editingAction,
            workflowId: savedWorkflowId
          };
          setEditingAction(updatedAction);

          // Update in Redux
          const actionIndex = actions.findIndex(a => a.id === editingAction.id);
          if (actionIndex >= 0) {
            dispatch({
              type: 'UPDATE_ACTION_ITEM',
              payload: {
                actionItem: updatedAction,
                index: actionIndex
              }
            });
          }

          // Save to backend
          if (dataId) {
            const updatedActions = actions.map(a =>
              a.id === editingAction.id ? updatedAction : a
            );
            await apis.updateDataActions(dataId, updatedActions);
          }
        }
      }

      Alert.alert('Success', 'Workflow saved successfully!');
      return savedWorkflowId;
    } catch (error) {
      console.error('❌ Failed to save workflow:', error);
      Alert.alert('Error', `Failed to save workflow: ${error.message || 'Unknown error'}`);
      throw error;
    }
  }, [currentWorkflowId, workflowNodes, workflowConnections, workflowData, editingAction, schemaData, dispatch, actions, dataId]);

  // Editor colors
  const editorColors = useMemo(() => ({
    background: theme ? '#ffffff' : '#1a1a1a',
    surface: theme ? '#f8f9fa' : '#2d2d2d',
    border: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
    text: theme ? '#333333' : '#e0e0e0',
    accent: 'rgb(90,192,234)',
    secondaryText: theme ? '#64748b' : '#94a3b8',
    cardBg: theme ? '#f1f5f9' : '#334155',
  }), [theme]);

  // Get action type info
  const getActionTypeInfo = useCallback((actionType: 'Create' | 'Read' | 'Update' | 'Delete') => {
    const types = {
      Create: { icon: 'add-circle', color: '#4CAF50', title: 'Create Action', description: 'Add actions that create new data' },
      Read: { icon: 'visibility', color: '#2196F3', title: 'Read Action', description: 'Add actions that retrieve and display data' },
      Update: { icon: 'edit', color: '#FF9800', title: 'Update Action', description: 'Add actions that modify existing data' },
      Delete: { icon: 'delete', color: '#F44336', title: 'Delete Action', description: 'Add actions that remove data' },
    };
    return types[actionType];
  }, []);

  // Extract available fields from schema
  const availableFields = useMemo(() => {
    if (!schemaData?.dataSchema) {
      console.log('⚠️ No schema data available for conditions');
      return [];
    }

    let fields = [];

    if (Array.isArray(schemaData.dataSchema)) {
      fields = schemaData.dataSchema.map(field => ({
        label: field.display?.label || field.label || field.title || field.name || 'Unknown Field',
        value: field.name || field.title || '',
        type: field.type || 'text'
      }));
    } else if (schemaData.dataSchema.fields) {
      fields = schemaData.dataSchema.fields.map(field => ({
        label: field.display?.label || field.label || field.title || field.name || 'Unknown Field',
        value: field.name || field.title || '',
        type: field.type || 'text'
      }));
    } else if (schemaData.dataSchema.properties) {
      fields = Object.entries(schemaData.dataSchema.properties).map(([name, def]: [string, any]) => ({
        label: def.title || def.content?.label || name,
        value: name,
        type: def.type || 'text'
      }));
    }

    console.log('📋 Available fields for conditions:', fields);
    return fields;
  }, [schemaData]);

  // Available operators
  const availableOperators = useMemo(() => [
    { label: 'Equals', value: 'equals', types: ['all'] },
    { label: 'Not Equals', value: 'not_equals', types: ['all'] },
    { label: 'Greater Than', value: 'greater_than', types: ['number', 'date'] },
    { label: 'Less Than', value: 'less_than', types: ['number', 'date'] },
    { label: 'Contains', value: 'contains', types: ['text', 'email', 'string'] },
    { label: 'Not Contains', value: 'not_contains', types: ['text', 'email', 'string'] },
    { label: 'Starts With', value: 'starts_with', types: ['text', 'email', 'string'] },
    { label: 'Ends With', value: 'ends_with', types: ['text', 'email', 'string'] },
    { label: 'Is Empty', value: 'is_empty', types: ['all'] },
    { label: 'Is Not Empty', value: 'is_not_empty', types: ['all'] }
  ], []);

  // Available actions when condition is met
  const availableConditionActions = useMemo(() => [
    { label: 'Skip this action', value: 'skip' },
    { label: 'Execute this action', value: 'execute' },
    { label: 'Show warning', value: 'warn' },
    { label: 'Block execution', value: 'block' },
    { label: 'Request approval', value: 'approval' },
  ], []);

  // Add a new condition
  const handleAddCondition = useCallback(() => {
    if (availableFields.length === 0) {
      Alert.alert(
        'No Fields Available',
        'The schema has no fields to create conditions from. Please ensure the data schema has defined fields.',
        [{ text: 'OK' }]
      );
      return;
    }

    const newCondition = {
      id: `condition-${Date.now()}`,
      field: availableFields[0]?.value || '',
      operator: 'equals',
      value: '',
      action: 'execute'
    };

    setConditions(prev => [...prev, newCondition]);
  }, [availableFields]);

  // Update a condition
  const handleUpdateCondition = useCallback((conditionId: string, field: string, value: any) => {
    setConditions(prev => prev.map(condition =>
      condition.id === conditionId
        ? { ...condition, [field]: value }
        : condition
    ));
  }, []);

  // Remove a condition
  const handleRemoveCondition = useCallback((conditionId: string) => {
    Alert.alert(
      'Remove Condition',
      'Are you sure you want to remove this condition?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: () => setConditions(prev => prev.filter(c => c.id !== conditionId))
        }
      ]
    );
  }, []);

  // Handle action card clicks - accept action object and check for workflow
  const handleActionClick = useCallback((action: any) => {
    console.log('🎯 Action card clicked in navigation hierarchy:', action);
    
    // Extract actionType from action object or use default
    const actionType = action?.actionType || action?.type || 'Create';
    const normalizedActionType = actionType.charAt(0).toUpperCase() + actionType.slice(1).toLowerCase() as 'Create' | 'Read' | 'Update' | 'Delete';

    // Store the action being edited
    setEditingAction(action);

    // Check if action has workflowId
    if (action?.workflowId) {
      console.log('🔗 Action has workflow, loading workflow:', action.workflowId);
      setCurrentWorkflowId(action.workflowId);
      // Load workflow data
      loadWorkflow(action.workflowId);
      // Start in action mode (user can switch to workflow mode)
      setActiveTab('type');
    } else {
      console.log('📝 Action has no workflow, opening action editor');
      setCurrentWorkflowId(null);
      setWorkflowData(null);
      setWorkflowNodes([]);
      setWorkflowConnections([]);
      // Start in action mode
      setActiveTab('type');
    }

    // Set form data from action if editing
    if (action) {
      setFormData({
        name: action.title || '',
        description: action.description || ''
      });
      setSelectedActionKey(action.key || 'email');
      setConditions(action.conditions || []);
    } else {
      setFormData({ name: '', description: '' });
      setSelectedActionKey('email');
      setConditions([]);
    }

    setSelectedActionType(normalizedActionType);
    setModalVisible(true);
  }, [loadWorkflow]);

  // Handle save action
  const handleSaveAction = useCallback(async () => {
    try {
      console.log('💾 Starting action save process...', {
        selectedActionType,
        selectedActionKey,
        formData,
        hasFormRef: !!formRef.current
      });

      // Get form data from the Formik ref (GenerateSchemaForm uses Formik internally)
      let configData: any = {};
      let templateImage = null;

      if (formRef.current?.values) {
        configData = formRef.current.values;
        console.log('📋 Got form values from Formik:', configData);

        // Extract template image if available (from stage_action which is the template object)
        if (configData.stage_action && typeof configData.stage_action === 'object') {
          templateImage = configData.stage_action.snapShot || configData.stage_action.thumbnail || configData.stage_action.image;
          console.log('🖼️ Template image found:', templateImage);
        }
      } else {
        console.warn('⚠️ No form ref or values available, using empty config');
      }

      // Calculate position based on action type and center point
      // Use the same logic as actionItems processing
      let calculatedPosition: { x: number; y: number };

      if (editingAction?.position && editingAction.position.x !== 0 && editingAction.position.y !== 0) {
        // Preserve existing position if it's valid
        calculatedPosition = editingAction.position;
      } else {
        // Calculate new position based on action type
        const viewportSize = Platform.select({
          ios: { width: 2500, height: 2500 },
          android: { width: 4000, height: 4000 },
          web: { width: 100000, height: 100000 },
          default: { width: 100000, height: 100000 }
        });
        const centerX = viewportSize.width / 2;
        const centerY = viewportSize.height / 2;

        // Calculate baseRadius proportional to viewport size
        // For 4000x4000 viewport, 500px = 12.5% of width (good spacing)
        // Scale proportionally for larger viewports
        const calculatedBaseRadius = Math.max(500, viewportSize.width * 0.125);

        // Direction mapping for action types
        // Use calculated baseRadius that scales with viewport size
        const typeDirections: Record<string, { angle: number; baseRadius: number }> = {
          'create': { angle: 0, baseRadius: calculatedBaseRadius },      // Right (east)
          'read': { angle: -90, baseRadius: calculatedBaseRadius },      // Up (north)
          'update': { angle: 90, baseRadius: calculatedBaseRadius },     // Down (south)
          'delete': { angle: 180, baseRadius: calculatedBaseRadius },   // Left (west)
          'secondary': { angle: 45, baseRadius: calculatedBaseRadius }  // Diagonal (southeast)
        };

        const actionType = selectedActionType.toLowerCase();
        const direction = typeDirections[actionType] || typeDirections['secondary'];
        const angleRad = (direction.angle * Math.PI) / 180;

        // Count existing user actions of the same type to stack them
        // Exclude default actions (they have _id starting with 'action-')
        const sameTypeActions = actions?.filter(a => {
          const aId = a.id || a._id || '';
          const aType = (a.actionType || a.type || 'secondary').toLowerCase();
          // Exclude default actions and match type
          return !aId.startsWith('action-create') &&
            !aId.startsWith('action-read') &&
            !aId.startsWith('action-update') &&
            !aId.startsWith('action-delete') &&
            aType === actionType;
        }) || [];

        const typeIndex = editingAction ? sameTypeActions.findIndex(a => a.id === editingAction.id) : sameTypeActions.length;
        const isHorizontal = Math.abs(direction.angle) === 0 || Math.abs(direction.angle) === 180;

        // Card dimensions for spacing calculation
        const cardWidth = 320; // Portrait card width
        const cardHeight = 400; // Portrait card height

        // Calculate spacing based on card dimensions to avoid overlaps
        const verticalSpacing = cardHeight + 50; // 50px padding between cards
        const horizontalSpacing = cardWidth + 50; // 50px padding between cards

        // Account for default action items when calculating index
        // Default actions are at index 0, so user actions start at index 1
        const totalActionsOfType = sameTypeActions.length + 1; // +1 for default action
        const adjustedIndex = typeIndex + 1; // User actions start after default

        // Calculate offset - center the stack including default action
        const offset = (adjustedIndex - (totalActionsOfType - 1) / 2) *
          (isHorizontal ? verticalSpacing : horizontalSpacing);

        if (isHorizontal) {
          calculatedPosition = {
            x: centerX + Math.cos(angleRad) * direction.baseRadius,
            y: centerY + Math.sin(angleRad) * direction.baseRadius + offset
          };
        } else {
          calculatedPosition = {
            x: centerX + Math.cos(angleRad) * direction.baseRadius + offset,
            y: centerY + Math.sin(angleRad) * direction.baseRadius
          };
        }

        console.log('📍 Calculated new position for action:', {
          actionType,
          center: { x: centerX, y: centerY },
          calculatedPosition,
          typeIndex,
          sameTypeCount: sameTypeActions.length
        });
      }

      // Create action data structure matching the expected format
      const newActionId = editingAction?.id || `action-${Date.now()}`;
      const actionData = {
        id: newActionId,
        _id: newActionId, // Include _id for hierarchy rendering
        key: selectedActionKey,
        actionType: selectedActionType.toLowerCase(),
        type: selectedActionType.toLowerCase(), // Also include 'type' field for consistency
        title: formData.name || `${selectedActionType} ${selectedActionKey} Action`,
        description: formData.description,
        icon: actionList.find(a => a.key === selectedActionKey)?.icon || 'cog',
        ...configData, // Spread the form config data
        templateImage: templateImage, // Include template image for display
        conditions: conditions.length > 0 ? conditions : undefined, // Add conditions if any
        workflowId: editingAction?.workflowId || currentWorkflowId || undefined, // Include workflowId if exists
        agenticFlow: editingAction?.agenticFlow || formRef.current?.agenticFlow || undefined, // Include agentic flow config if exists
        position: calculatedPosition,
        x: calculatedPosition.x,
        y: calculatedPosition.y,
        createdAt: editingAction?.createdAt || new Date().toISOString(),
        component: null, // Explicitly set to null (reducer will do this anyway)
      };

      console.log('🎯 Dispatching ADD_ACTION with data:', actionData);

      // Add to Redux store with correct payload structure
      dispatch({
        type: ADD_ACTION,
        payload: {
          actionData: actionData
        }
      });


      // Save to backend if dataId exists
      if (dataId) {
        try {
          // If editing existing action, update it; otherwise add new
          const existingIndex = editingAction ? actions.findIndex(a => a.id === editingAction.id) : -1;
          const updatedActions = existingIndex >= 0
            ? actions.map((a, i) => i === existingIndex ? actionData : a)
            : [...actions, actionData];
          await apis.updateDataActions(dataId, updatedActions);
          console.log('✅ Action saved to backend:', updatedActions.length, 'total actions');
        } catch (error) {
          console.error('❌ Error saving to backend:', error);
          // Don't block the UI - action is already in Redux
        }
      }

      Alert.alert('Success', 'Action added successfully!');

      // Reset form and close
      setFormData({ name: '', description: '' });
      setConditions([]); // Reset conditions
      setActiveTab('type'); // Reset to first tab for next time
      setEditingAction(null); // Clear editing action
      setCurrentWorkflowId(null); // Clear workflow ID
      setWorkflowData(null); // Clear workflow data
      setModalVisible(false);

      console.log('🎉 Action save completed successfully');
    } catch (error) {
      console.error('❌ Error saving action:', error);
      Alert.alert('Error', `Failed to save action: ${error.message}`);
    }
  }, [selectedActionType, selectedActionKey, formData, conditions, dispatch, dataId, actions, editingAction, currentWorkflowId]);

  // Calculate viewport center - in manual mode, use actual viewport center
  // horizontalOffset only affects circular/grid strategies, not manual positioning
  // This must be defined before primaryAction and actionItems useMemo hooks
  const viewportSize = Platform.select({
    ios: { width: 2500, height: 2500 },
    android: { width: 4000, height: 4000 },
    web: { width: 100000, height: 100000 }, // Infinite zoomable viewport for web
    default: { width: 100000, height: 100000 } // Default to large size for web
  });
  // For manual positioning, center is simply viewport center (0,0 is top-left)
  const calculatedCenterX = viewportSize.width / 2;
  const calculatedCenterY = viewportSize.height / 2;

  // Calculate baseRadius proportional to viewport size
  // For 4000x4000 viewport, 500px = 12.5% of width (good spacing)
  // Scale proportionally for larger viewports
  const baseRadius = Math.max(500, viewportSize.width * 0.125); // At least 500px, or 12.5% of viewport width

  // Convert schema data to primary action item
  const primaryAction = useMemo(() => {
    if (!schemaData) return undefined;

    // Validate primary action position - must be within viewport + small margin
    const maxValidPosition = viewportSize.width + 500;
    const minValidPosition = -500;
    const hasValidPosition = schemaData.x !== undefined &&
      schemaData.y !== undefined &&
      schemaData.x >= minValidPosition &&
      schemaData.x <= maxValidPosition &&
      schemaData.y >= minValidPosition &&
      schemaData.y <= maxValidPosition;

    const finalX = hasValidPosition ? schemaData.x : calculatedCenterX;
    const finalY = hasValidPosition ? schemaData.y : calculatedCenterY;

    if (__DEV__) {
      console.log('🎯 Primary action position validation (ActionHandler):', {
        schemaDataX: schemaData.x,
        schemaDataY: schemaData.y,
        hasValidPosition,
        viewportSize: viewportSize.width,
        willUse: { x: finalX, y: finalY }
      });
    }

    return {
      ...schemaData, // Spread first
      _id: schemaData.id || 'primary-action',
      title: schemaData.title || 'Primary Action',
      description: schemaData.description,
      dataSchema: schemaData.dataSchema,
      type: 'primary',
      // Override x/y AFTER spread to ensure valid positions
      x: finalX,
      y: finalY,
      position: { x: finalX, y: finalY },
      _positionCorrected: !hasValidPosition // Flag to force action recalculation
    };
  }, [schemaData, calculatedCenterX, calculatedCenterY, viewportSize]);

  console.log('🎯 Primary action processing:', {
    hasSchemaData: !!schemaData,
    primaryAction: primaryAction,
    schemaDataId: schemaData?.id,
    schemaDataTitle: schemaData?.title,
    schemaDataSchema: schemaData?.dataSchema
  });

  // Convert workflow nodes to action items format
  const workflowNodeItems = useMemo(() => {
    return workflowNodes.map(node => {
      const nodeTypeDef = getNodeType(node.type);
      const category = nodeTypeDef?.category || 'action';
      return {
        _id: node.id,
        id: node.id,
        title: node.name,
        description: `Workflow node: ${node.type}`,
        type: 'workflow',
        nodeType: category,
        isWorkflowNode: true,
        x: node.position.x,
        y: node.position.y,
        config: node.config,
        key: node.type,
        actionType: 'workflow',
        icon: nodeTypeDef?.icon || 'settings',
        workflowNode: { ...node, category } // Store original node with category
      };
    });
  }, [workflowNodes]);

  // Convert actions to action items with proper type mapping and direction-based positioning
  const actionItems = useMemo(() => {
    if (!actions || actions.length === 0) return [];

    // Filter out default action items (they have _id starting with 'action-')
    // Default actions are handled separately in defaultActionItems
    const userActions = actions.filter(action => {
      const actionId = action.id || action._id || '';
      // Exclude default action items (action-create, action-read, action-update, action-delete)
      return !actionId.startsWith('action-create') &&
        !actionId.startsWith('action-read') &&
        !actionId.startsWith('action-update') &&
        !actionId.startsWith('action-delete');
    });

    if (userActions.length === 0) return [];

    // Group actions by type to calculate positions
    const actionsByType: Record<string, any[]> = {
      'create': [],
      'read': [],
      'update': [],
      'delete': [],
      'secondary': []
    };

    // First pass: categorize all user actions
    userActions.forEach((action, index) => {
      // Use actionType from action data if available, otherwise fallback to key mapping
      let actionType = 'secondary';
      if (action.actionType) {
        // Use the explicit actionType from the dialog (create, read, update, delete)
        actionType = action.actionType;
      } else if (action.key) {
        // Fallback: Map common action keys to their corresponding default types
        const keyToTypeMap: Record<string, string> = {
          'email': 'create',
          'whatsapp': 'create',
          'calendar': 'create',
          'project': 'create',
          'call': 'create',
          'read': 'read',
          'view': 'read',
          'display': 'read',
          'update': 'update',
          'edit': 'update',
          'modify': 'update',
          'delete': 'delete',
          'remove': 'delete'
        };
        actionType = keyToTypeMap[action.key] || 'secondary';
      }

      // Group by type
      const typeKey = actionType.toLowerCase();
      // Ensure both actionType and type fields are set correctly for consistency
      const processedAction = {
        ...action,
        actionType: actionType.toLowerCase(), // Ensure actionType is set
        type: actionType.toLowerCase(), // Also set type field for DraggableActionCard compatibility
        _calculatedType: actionType
      };

      if (actionsByType[typeKey]) {
        actionsByType[typeKey].push(processedAction);
      } else {
        actionsByType['secondary'].push(processedAction);
      }
    });

    // Direction mapping for action types (angles in degrees, converted to radians)
    // Use calculated baseRadius that scales with viewport size
    const typeDirections: Record<string, { angle: number; baseRadius: number }> = {
      'create': { angle: 0, baseRadius: baseRadius },      // Right (east)
      'read': { angle: -90, baseRadius: baseRadius },      // Up (north)
      'update': { angle: 90, baseRadius: baseRadius },    // Down (south)
      'delete': { angle: 180, baseRadius: baseRadius },   // Left (west)
      'secondary': { angle: 45, baseRadius: baseRadius }  // Diagonal (southeast)
    };

    // Calculate positions for each action based on type and index within type
    const processedActions: any[] = [];

    Object.keys(actionsByType).forEach(typeKey => {
      const typeActions = actionsByType[typeKey];
      const direction = typeDirections[typeKey] || typeDirections['secondary'];

      typeActions.forEach((action, typeIndex) => {
        // Preserve existing position if it exists (user has dragged it)
        // Check if action has a position object or both x and y coordinates
        // Treat (0, 0) as invalid position (likely uninitialized) - recalculate it
        // Also check if position is in old coordinate system (around 2000, 2000 for old 4000x4000 viewport)
        let calculatedPosition: { x: number; y: number } | null = null;
        let existingPosition: { x: number; y: number } | null = null;

        if (action.position && typeof action.position === 'object' && action.position.x !== undefined && action.position.y !== undefined) {
          existingPosition = { x: action.position.x, y: action.position.y };
        } else if (action.x !== undefined && action.y !== undefined) {
          existingPosition = { x: action.x, y: action.y };
        }

        // Validate existing position:
        // 1. Must not be (0, 0) - uninitialized
        // 2. Must be within reasonable bounds of current viewport (not from old coordinate system)
        // 3. Must not be from old random positioning (Math.random() * 200 = 0-200 range)
        // For web (100000x100000), valid positions should be roughly between 0-100000
        // Old positions from 4000x4000 viewport would be around 0-4000, which is too small
        // Old positions from 2500x2500 viewport (iOS) would be around 0-2500, which is too small
        // Old random positions would be around 0-200, which is way too small
        if (existingPosition) {
          const isZero = existingPosition.x === 0 && existingPosition.y === 0;

          // Check if position is from old random positioning (0-200 range)
          // Also check for positions that are clearly too small for the current viewport
          const isOldRandomPosition = (existingPosition.x < 500 && existingPosition.y < 500 &&
            (existingPosition.x > 0 || existingPosition.y > 0)) ||
            // For large viewports (web), any position < 1000 is suspicious
            (viewportSize.width >= 50000 && existingPosition.x < 1000 && existingPosition.y < 1000);

          // Check if position is from old coordinate system
          // Old viewports: 4000x4000 (center ~2000) or 2500x2500 (center ~1250)
          // New viewport: 100000x100000 (center 50000) or 4000x4000 (center 2000) or 2500x2500 (center 1250)
          const distanceFromCenter = Math.sqrt(
            Math.pow(existingPosition.x - calculatedCenterX, 2) +
            Math.pow(existingPosition.y - calculatedCenterY, 2)
          );

          // If position is very close to old centers (2000, 2000) or (1250, 1250) but far from current center, recalculate
          const isOldCenter = (Math.abs(existingPosition.x - 2000) < 1000 && Math.abs(existingPosition.y - 2000) < 1000) ||
            (Math.abs(existingPosition.x - 1250) < 500 && Math.abs(existingPosition.y - 1250) < 500);
          const isFarFromCurrentCenter = distanceFromCenter > viewportSize.width * 0.1; // More than 10% of viewport away

          // For web (100000x100000), positions should be much larger. If both x and y are less than 1000, it's likely invalid
          const isTooSmallForViewport = viewportSize.width >= 50000 &&
            existingPosition.x < 1000 && existingPosition.y < 1000;

          // Additional check: if position is way too far from center (more than 50% of viewport), it's likely invalid
          const isWayTooFarFromCenter = distanceFromCenter > viewportSize.width * 0.5;

          // For web viewports, valid positions should be in a reasonable range around center
          // Center is 50000, so valid positions should be roughly between 40000-60000
          const isValidRangeForWeb = viewportSize.width >= 50000 ?
            (existingPosition.x > 10000 && existingPosition.x < 90000 &&
              existingPosition.y > 10000 && existingPosition.y < 90000) : true;

          // Check if position is close to primary action (within 200px and on 100px grid)
          // Also force recalculation if primary position was just corrected
          let isCloseToPrimary = true; // Default to true if no primary action
          const gridSpacing = 100;
          const maxDistance = 200;
          const primaryPositionWasCorrected = (primaryAction as any)?._positionCorrected === true;

          // CRITICAL: Always recalculate if position is more than 200px from primary action
          // This ensures actions are always 100px away, not thousands of pixels
          let mustRecalculate = false;
          if (primaryAction && primaryAction.x !== undefined && primaryAction.y !== undefined) {
            const deltaX = Math.abs(existingPosition.x - primaryAction.x);
            const deltaY = Math.abs(existingPosition.y - primaryAction.y);
            mustRecalculate = deltaX > maxDistance || deltaY > maxDistance;

            // If primary position was corrected, always recalculate
            if (primaryPositionWasCorrected) {
              isCloseToPrimary = false;
              mustRecalculate = true;
            } else {
              const tolerance = 10; // 10px tolerance for grid alignment
              const isOnGrid = (deltaX <= gridSpacing + tolerance && deltaY <= tolerance) ||
                (deltaY <= gridSpacing + tolerance && deltaX <= tolerance);
              const isWithinMaxDistance = deltaX <= maxDistance && deltaY <= maxDistance;
              isCloseToPrimary = isOnGrid && isWithinMaxDistance;
            }
          }

          if (!mustRecalculate && !isZero && !isOldRandomPosition && !(isOldCenter && isFarFromCurrentCenter) &&
            !isTooSmallForViewport && !isWayTooFarFromCenter && isValidRangeForWeb && isCloseToPrimary) {
            // Position looks valid - preserve it
            calculatedPosition = existingPosition;

            if (__DEV__) {
              console.log(`✅ Preserving existing position for ${action.title || action.id}:`, {
                position: existingPosition,
                distanceFromCenter,
                viewportCenter: { x: calculatedCenterX, y: calculatedCenterY }
              });
            }
          } else {
            if (__DEV__) {
              const reasons = [];
              if (mustRecalculate) reasons.push('MORE THAN 200px FROM PRIMARY - FORCING RECALCULATION');
              if (isZero) reasons.push('zero position');
              if (isOldRandomPosition) reasons.push('old random position (< 500 or < 1000 for large viewports)');
              if (isTooSmallForViewport) reasons.push('too small for viewport');
              if (isOldCenter && isFarFromCurrentCenter) reasons.push('old coordinate system center');
              if (isWayTooFarFromCenter) reasons.push('way too far from center');
              if (!isValidRangeForWeb) reasons.push('outside valid range for web viewport');
              if (!isCloseToPrimary) reasons.push('not on 100px grid or too far from primary');

              console.log(`🔄 Recalculating position for ${action.title || action.id}:`, {
                reasons: reasons.length > 0 ? reasons : ['unknown'],
                existingPosition,
                primaryActionPos: primaryAction ? { x: primaryAction.x, y: primaryAction.y } : null,
                distanceFromPrimary: primaryAction && primaryAction.x !== undefined ?
                  Math.sqrt(Math.pow(existingPosition.x - primaryAction.x, 2) + Math.pow(existingPosition.y - primaryAction.y, 2)) : null,
                viewportCenter: { x: calculatedCenterX, y: calculatedCenterY },
                viewportSize: viewportSize.width,
                checks: {
                  mustRecalculate,
                  isZero,
                  isOldRandomPosition,
                  isTooSmallForViewport,
                  isOldCenter,
                  isFarFromCurrentCenter,
                  isWayTooFarFromCenter,
                  isValidRangeForWeb,
                  isCloseToPrimary,
                  distanceFromCenter
                }
              });
            }
          }
          // Otherwise, recalculate below
        }

        // Only calculate new position if action doesn't have a position
        if (!calculatedPosition) {
          // Use primary action position if available, otherwise use viewport center
          const referenceX = primaryAction?.x ?? calculatedCenterX;
          const referenceY = primaryAction?.y ?? calculatedCenterY;
          const gridSpacing = 100; // 100px grid spacing

          // Position actions in a grid pattern: right, left, top, bottom
          // Use action type to determine direction, then stack by index
          let gridPosition = { x: referenceX, y: referenceY };

          // Map action types to grid directions
          const typeToDirection: Record<string, number> = {
            'create': 0,  // Right
            'delete': 1,  // Left
            'read': 2,    // Top
            'update': 3,  // Bottom
            'secondary': 0 // Default to right
          };

          const directionIndex = typeToDirection[typeKey.toLowerCase()] ?? 0;

          // Calculate which position in the grid (for stacking multiple actions of same type)
          // typeIndex is 0-based within the type, so first action of type is at gridSpacing, second at 2*gridSpacing, etc.
          const gridOffset = gridSpacing; // Always 100px from primary

          switch (directionIndex) {
            case 0: // Right
              gridPosition = {
                x: referenceX + gridOffset,
                y: referenceY
              };
              break;
            case 1: // Left
              gridPosition = {
                x: referenceX - gridOffset,
                y: referenceY
              };
              break;
            case 2: // Top
              gridPosition = {
                x: referenceX,
                y: referenceY - gridOffset
              };
              break;
            case 3: // Bottom
              gridPosition = {
                x: referenceX,
                y: referenceY + gridOffset
              };
              break;
          }

          // Use the grid position calculated above
          calculatedPosition = gridPosition;

          if (__DEV__) {
            console.log(`📍 Calculated position for ${action.title || action.id}:`, {
              actionType: typeKey,
              reference: { x: referenceX, y: referenceY },
              calculatedPosition: gridPosition,
              viewportCenter: { x: calculatedCenterX, y: calculatedCenterY },
              gridSpacing,
              directionIndex,
              typeIndex
            });
          }
        }

        // Create action item with calculated position
        // Spread action last to preserve calculated position (calculatedPosition takes precedence)
        processedActions.push({
          ...action, // Spread first to get all action properties
          _id: action.id || `action-${processedActions.length}`,
          title: action.title || `Action ${processedActions.length + 1}`,
          description: action.description,
          type: action._calculatedType || 'secondary',
          key: action.key,
          actionType: action.actionType || action._calculatedType,
          // Override position with calculated position (must come after spread)
          x: calculatedPosition.x,
          y: calculatedPosition.y,
          position: calculatedPosition
        });
      });
    });

    return processedActions;
  }, [actions, primaryAction, calculatedCenterX, calculatedCenterY, baseRadius]);

  console.log('📋 Action items processing result:', {
    originalActionsCount: actions?.length || 0,
    processedActionItemsCount: actionItems.length,
    actionItems: actionItems.map(item => ({
      id: item._id,
      title: item.title,
      type: item.type,
      actionType: item.actionType,
      key: item.key
    }))
  });

  // Add default action cards (always available) with direction-based positioning
  // Memoize to prevent recreation on every render
  const defaultActionItems = useMemo(() => {
    // Always use the viewport center as the reference point for positioning
    // All actions should be positioned relative to the center point
    const centerX = calculatedCenterX;
    const centerY = calculatedCenterY;

    // Direction mapping for default actions
    const defaultActions = [
      {
        _id: 'action-create',
        title: '➕ Create Action',
        description: 'Allows users to create new records in the schema',
        type: 'create',
        dataSchema: [
          { name: 'name', type: 'string', required: true },
          { name: 'email', type: 'string', required: true }
        ]
      },
      {
        _id: 'action-read',
        title: '👁️ Read Action',
        description: 'Retrieves and displays existing records',
        type: 'read',
        dataSchema: [
          { name: 'id', type: 'string' },
          { name: 'filters', type: 'object' }
        ]
      },
      {
        _id: 'action-update',
        title: '✏️ Update Action',
        description: 'Modifies existing records in the schema',
        type: 'update',
        dataSchema: [
          { name: 'id', type: 'string', required: true },
          { name: 'changes', type: 'object', required: true }
        ]
      },
      {
        _id: 'action-delete',
        title: '🗑️ Delete Action',
        description: 'Removes records from the schema',
        type: 'delete',
        dataSchema: [
          { name: 'id', type: 'string', required: true }
        ]
      }
    ];

    // Position each default action based on its type
    return defaultActions.map(action => {
      const typeDirections: Record<string, { angle: number; baseRadius: number }> = {
        'create': { angle: 0, baseRadius: baseRadius },      // Right (east)
        'read': { angle: -90, baseRadius: baseRadius },      // Up (north)
        'update': { angle: 90, baseRadius: baseRadius },     // Down (south)
        'delete': { angle: 180, baseRadius: baseRadius }     // Left (west)
      };

      const direction = typeDirections[action.type] || { angle: 0, baseRadius: baseRadius };
      const angleRad = (direction.angle * Math.PI) / 180;

      const x = centerX + Math.cos(angleRad) * direction.baseRadius;
      const y = centerY + Math.sin(angleRad) * direction.baseRadius;

      return {
        ...action,
        x,
        y,
        position: { x, y }
      };
    });
  }, [primaryAction, calculatedCenterX, calculatedCenterY, baseRadius]); // Include calculated centers and baseRadius to recalculate when they change

  // Add default primary schema if needed
  // Memoize to prevent recreation on every render
  const defaultPrimaryAction = useMemo(() => {
    return {
      _id: 'test-primary-schema',
      title: '🏛️ Main Data Schema',
      description: 'This is the central schema that defines the data structure for your application',
      type: 'primary-schema',
      x: calculatedCenterX,
      y: calculatedCenterY,
      position: { x: calculatedCenterX, y: calculatedCenterY },
      dataSchema: [
        { name: 'id', type: 'string', required: true, description: 'Unique identifier' },
        { name: 'name', type: 'string', required: true, description: 'Display name' },
        { name: 'email', type: 'string', required: true, description: 'Email address' },
        { name: 'created_at', type: 'datetime', required: true, description: 'Creation timestamp' },
        { name: 'status', type: 'enum', options: ['active', 'inactive'], description: 'Current status' }
      ]
    };
  }, [calculatedCenterX, calculatedCenterY]); // Include calculated centers

  // Combine user actions and workflow nodes (removed default action items - they're now accessed via slide panel)
  // Memoize to prevent unnecessary re-renders that cause position resets
  const finalActionItems = useMemo(() => {
    return [...actionItems, ...workflowNodeItems];
  }, [actionItems, workflowNodeItems]);

  const finalPrimaryAction = useMemo(() => {
    return primaryAction || defaultPrimaryAction;
  }, [primaryAction, defaultPrimaryAction]);

  // Helper function to get color for action type
  const getColorForActionType = useCallback((type: string): string => {
    const colors: Record<string, string> = {
      'create': '#4CAF50',    // Green
      'read': '#2196F3',      // Blue
      'update': '#FF9800',    // Orange
      'delete': '#F44336',    // Red
      'secondary': '#9C27B0'  // Purple for custom actions
    };
    return colors[type] || '#2196F3';
  }, []);

  // Create links: defaults to primary schema, added actions to their parent default
  // Memoize to prevent recalculation on every render
  const actionLinks = useMemo(() => {
    const links = [];

    if (finalActionItems.length > 0) {
      console.log('🔗 Creating links between actions and primary schema:', {
        actionItemsCount: finalActionItems.length,
        actions: finalActionItems.map(item => ({ id: item._id, type: item.type, title: item.title }))
      });

      // Link user actions directly to primary schema (default actions removed - accessed via slide panel)
      const userActions = finalActionItems;

      console.log('📊 Action linking:', {
        userActionsCount: userActions.length,
        userActions: userActions.map(a => ({ id: a._id, type: a.type, title: a.title }))
      });

      // Link all user actions to primary schema
      if (finalPrimaryAction && userActions.length > 0) {
        userActions.forEach(userAction => {
          const link = {
            sourceId: finalPrimaryAction._id,
            targetId: userAction._id,
            type: `flows_to_${userAction.type}`,
            color: getColorForActionType(userAction.type),
            width: 2
          };

          console.log('🔗 Created primary-to-user-action link:', link);
          links.push(link);
        });
      }

    } else {
      console.log('⚠️ No links created:', {
        hasPrimaryAction: !!finalPrimaryAction,
        actionItemsCount: finalActionItems.length,
        primaryAction: finalPrimaryAction,
        actionItems: finalActionItems
      });
    }

    return links;
  }, [finalActionItems, finalPrimaryAction, getColorForActionType]);

  // Action Link Handler ref for imperative updates
  const actionLinkHandlerRef = useRef<ActionLinkHandlerHandle>(null);

  // Sync positions from BaseHierarchy to actionPositionsRef for link rendering
  useEffect(() => {
    const syncPositions = () => {
      const allPositions = baseHierarchyRef.current?.getAllItemPositions() || {};
      console.log('🔄 Syncing positions for links:', {
        allPositionsCount: Object.keys(allPositions).length,
        allPositions: allPositions,
        currentActionPositionsCount: Object.keys(actionPositionsRef.current).length
      });

      // Update actionPositionsRef with all positions from BaseHierarchy
      actionPositionsRef.current = { ...allPositions };

      // Force link handler update
      if (actionLinkHandlerRef.current?.forceUpdate) {
        actionLinkHandlerRef.current.forceUpdate();
      }
    };

    // Sync positions after a short delay to ensure BaseHierarchy has initialized
    const timeoutId = setTimeout(syncPositions, 100);

    return () => clearTimeout(timeoutId);
  }, [finalActionItems, finalPrimaryAction]); // Re-sync when items change

  // Additional sync after BaseHierarchy has had time to initialize positions
  useEffect(() => {
    const syncAfterMount = () => {
      const allPositions = baseHierarchyRef.current?.getAllItemPositions() || {};
      console.log('🔄 Post-mount position sync:', {
        allPositionsCount: Object.keys(allPositions).length,
        allPositions: allPositions
      });

      // Update actionPositionsRef with all positions from BaseHierarchy
      actionPositionsRef.current = { ...allPositions };

      // Force link handler update
      if (actionLinkHandlerRef.current?.forceUpdate) {
        actionLinkHandlerRef.current.forceUpdate();
      }
    };

    // Sync after BaseHierarchy has mounted and calculated positions
    const timeoutId = setTimeout(syncAfterMount, 500);

    return () => clearTimeout(timeoutId);
  }, []); // Run once after mount

  useImperativeHandle(ref, () => ({
    zoomDisabled: () => {
      console.log('🔒 Zoom disabled');
    },
    zoomEnabled: () => {
      console.log('🔓 Zoom enabled');
    },
    appendPosition: (values: any, i: number) => {
      console.log('📍 Append position:', values, i);
    },
    moveTo: (x: number, y: number) => {
      baseHierarchyRef.current?.moveTo(x, y);
    },
    zoomBy: (zoom: number) => {
      baseHierarchyRef.current?.zoomBy(zoom);
    },
    // New centering methods using correct moveTo formula
    centerView: () => {
      console.log('🎯 Centering view on all actions');
      baseHierarchyRef.current?.centerView();
    },
    centerOnPrimary: () => {
      console.log('🎯 Centering view on primary schema');
      baseHierarchyRef.current?.centerOnPrimary();
    },
    centerOnItem: (itemId: string) => {
      console.log('🎯 Centering view on item:', itemId);
      baseHierarchyRef.current?.centerOnItem(itemId);
    },
    zoomableViewMethods: new Proxy({}, {
      get: (_, methodName: string) =>
        (...args: unknown[]) => {
          console.log('🔍 Zoomable method called:', methodName, args);
          return baseHierarchyRef.current?.[methodName]?.(...args);
        },
    }),
  }));

  const handleSnapWrapper = useCallback((target: any, source: any) => {
    console.log('🎯 Snap handled:', { target, source });
    handleSnap(target);
  }, [handleSnap]);

  const handleActionPositionChange = useCallback((actionId: string, position: any) => {
    // Update positions ref for direct access by link handler
    // BaseHierarchy manages positions internally via DraggableActionCard's useSharedValue
    // We just track them here for link rendering
    actionPositionsRef.current[actionId] = position;

    // Trigger ActionLinkHandler update efficiently using imperative handle
    if (actionLinkHandlerRef.current?.forceUpdate) {
      actionLinkHandlerRef.current.forceUpdate();
    }
  }, []); // Empty dependency array since we're only using refs

  const getAllActionPositions = useCallback(() => {
    return baseHierarchyRef.current?.getAllItemPositions() || {};
  }, []); // Empty dependency array since we're only using refs

  // Render function for the primary schema component
  // Use useRef to stabilize zoomVars object reference
  const primarySchemaZoomVarsRef = useRef({
    offsetX: -500,
    offsetY: -500,
    zoomLevel: 0.4,
    originalHeight: 0,
    originalWidth: 0,
    originalPageX: 0,
    originalPageY: 0
  });

  // Stable transform object to prevent prop changes
  const stableTransformRef = useRef({ translateX: 0, translateY: 0 });

  const renderSchemaItem = useCallback((schema: any, position: any, onPositionChange: any, currentZoomLevel = 0.4) => {
  // Render callback is called on pan/zoom by BaseHierarchy, but React.memo prevents actual re-renders
    return (
      <DraggableActionCard
        action={schema}
        initialPosition={position}
        onPositionChange={(newPos) => {
          // Don't allow position changes for primary schema
        }}
        onSnap={handleSnapWrapper}
        dragEnabled={false} // Primary schema shouldn't be draggable
        portrait={true}
        showArrows={false} // Primary schema doesn't need arrows
        isPrimarySchema={true} // Special flag for styling
        zoomVars={primarySchemaZoomVarsRef.current}
        accelerateValue={1}
        transform={stableTransformRef.current}
        getAllActionPositions={getAllActionPositions}
        index={-1} // Primary schema has special index
        onActionClick={(action) => {
          // Open slide panel when primary schema card is clicked
          console.log('🎯 Primary schema clicked - opening slide panel', action);
          setModalVisible(true);
          setActiveTab('type');
        }}
        dataId={dataId}
      />
    );
  }, [handleSnapWrapper, getAllActionPositions, dataId]); // Memoize with dependencies - setModalVisible and setActiveTab are stable

  // Render function for action items
  // Use useRef to stabilize zoomVars object reference
  const actionItemZoomVarsRef = useRef({
    offsetX: -500,
    offsetY: -500,
    zoomLevel: 0.4,
    originalHeight: 0,
    originalWidth: 0,
    originalPageX: 0,
    originalPageY: 0
  });

  // Cache for action indices to avoid findIndex on every render
  const actionIndexCache = useRef<Map<string, number>>(new Map());

  // Update cache when finalActionItems changes
  useEffect(() => {
    const newCache = new Map<string, number>();
    finalActionItems.forEach((item, index) => {
      newCache.set(item._id, index);
    });
    actionIndexCache.current = newCache;
  }, [finalActionItems]);

  const renderActionItem = useCallback((action: any, position: any, onPositionChange: any, currentZoomLevel = 0.4) => {
    
    // If this is the primary schema item, use the schema renderer
    if (finalPrimaryAction && action._id === finalPrimaryAction._id) {
      return renderSchemaItem(action, position, onPositionChange, currentZoomLevel);
    }

    // Check if this is a workflow node
    const isWorkflowNode = action.isWorkflowNode || action.nodeType !== undefined;
    const nodeType = action.workflowNode?.type || action.type;
    const nodeCategory = action.nodeType;

    // Check if this is an agentic flow
    const isAgenticFlow = action.agenticFlow?.enabled || action.config?.agenticFlow?.enabled;
    const agenticFlowConfig = action.agenticFlow || action.config?.agenticFlow;

    // Get cached index instead of computing on every render
    const cachedIndex = actionIndexCache.current.get(action._id) ?? 0;

    return (
      <DraggableActionCard
        action={action}
        initialPosition={position}
        onPositionChange={(newPos) => {
          // Update position in actionPositionsRef for link rendering
          // BaseHierarchy manages positions internally, we just track them for links
          actionPositionsRef.current[action._id] = newPos;
          onPositionChange(newPos);

          // Update workflow node position if it's a workflow node
          if (isWorkflowNode && action.workflowNode) {
            // Handle workflow node position update if needed
          }
        }}
        onSnap={handleSnapWrapper}
        dragEnabled={true} // Allow dragging for all action cards
        portrait={true}
        showArrows={actionLinks.length < 1}
        zoomVars={actionItemZoomVarsRef.current}
        accelerateValue={1}
        transform={stableTransformRef.current}
        getAllActionPositions={getAllActionPositions}
        index={cachedIndex}
        onActionClick={handleActionClick}
        dataId={dataId}
        isWorkflowNode={isWorkflowNode}
        nodeType={nodeType}
        nodeCategory={nodeCategory}
        isAgenticFlow={isAgenticFlow}
        agenticFlowConfig={agenticFlowConfig}
        onWorkflowNodeClick={(nodeId) => {
          console.log('🔧 Workflow node clicked:', nodeId);
          // Could open node editor here
        }}
      />
    );
  }, [handleSnapWrapper, getAllActionPositions, actionLinks.length, finalPrimaryAction, renderSchemaItem, handleActionClick, dataId]); // Removed finalActionItems from deps

  return (
    <>
    <BaseHierarchy
      ref={baseHierarchyRef}
      items={finalActionItems}
      primaryItem={finalPrimaryAction} // Base component - view will auto-center on this schema
      links={actionLinks}
      isLoading={isLoading}
      onItemPositionChange={handleActionPositionChange}
      onSnap={handleSnapWrapper}
      renderItem={renderActionItem}
      renderLinkHandler={({ itemPositionsRef, links, theme, zoomLevel }) => (
        <ActionLinkHandler
          ref={actionLinkHandlerRef}
          actionPositionsRef={actionPositionsRef}
          links={links}
          theme={theme}
          zoomLevel={zoomLevel}
        />
      )}
      // Configuration
        // Platform-specific viewport sizes to prevent Metal texture size errors on iOS
        // Metal has a maximum texture size of 8192x8192
        // ReactNativeZoomableView may create textures up to viewportSize * maxZoom
        // For iOS: 2500 * 2.5 = 6250 (safe), for others: 4000 * 3 = 12000 (but not Metal-limited)
        // Web: 100000x100000 for effectively infinite zoomable canvas
        viewportSize={Platform.select({
          ios: { width: 2500, height: 2500 }, // Conservative size for Metal (2500 * 2.5 = 6250 < 8192)
          android: { width: 4000, height: 4000 },
          web: { width: 100000, height: 100000 }, // Infinite zoomable viewport for web
          default: { width: 100000, height: 100000 } // Default to large size for web
        })}
      gridSize={50}
      initialZoom={0.5}
      minZoom={0.2}
        maxZoom={Platform.select({
          ios: 2.5, // Reduced to prevent texture size issues (2500 * 2.5 = 6250 < 8192)
          default: 3
        })}
        positioningStrategy="manual" // Use manual positioning to respect direction-based positions
      centerSpacing={500}
        horizontalOffset={0.25} // Shift content 25% to the left to leave space on the right
      showGrid={true}
      showDebugInfo={__DEV__}
    />

      {/* Action Editor Modal - Slides in from right */}
      <Modal
        animationType="none"
        transparent={true}
        visible={modalVisible}
        onRequestClose={() => setModalVisible(false)}
      >
        {/* Backdrop */}
        <MotiView
          from={{ opacity: 0 }}
          animate={{ opacity: modalVisible ? 0.5 : 0 }}
          exit={{ opacity: 0 }}
          transition={{ type: 'timing', duration: 300 }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'black',
          }}
        >
          <Pressable
            style={{ flex: 1 }}
            onPress={() => setModalVisible(false)}
          />
        </MotiView>

        {/* Editor Panel - Slides in from right */}
        <ActionEditorSlidePanel
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          selectedActionType={selectedActionType}
          setSelectedActionType={setSelectedActionType}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          selectedActionKey={selectedActionKey}
          setSelectedActionKey={setSelectedActionKey}
          formData={formData}
          setFormData={setFormData}
          conditions={conditions}
          setConditions={setConditions}
          formRef={formRef}
          actions={actions}
          schemaData={schemaData}
          onSave={handleSaveAction}
          getActionTypeInfo={getActionTypeInfo}
          editorColors={editorColors}
          availableFields={availableFields}
          availableOperators={availableOperators}
          availableConditionActions={availableConditionActions}
          handleAddCondition={handleAddCondition}
          handleUpdateCondition={handleUpdateCondition}
          handleRemoveCondition={handleRemoveCondition}
          workflowId={currentWorkflowId || undefined}
          workflowNodes={workflowNodes}
          workflowConnections={workflowConnections}
          onWorkflowNodeMove={handleWorkflowNodeMove}
          onWorkflowNodeAdd={handleWorkflowNodeAdd}
          onWorkflowSave={saveWorkflow}
          editorMode={currentWorkflowId ? (activeTab === 'workflow' || activeTab === 'workflowNodes' || activeTab === 'history' ? 'workflow' : 'action') : 'action'}
          onEditorModeChange={(mode) => {
            if (mode === 'workflow') {
              setActiveTab('workflow');
            } else {
              setActiveTab('type');
            }
          }}
          currentWorkflowId={currentWorkflowId}
          isLoadingWorkflow={isLoadingWorkflow}
          editingAction={editingAction}
        />
      </Modal>
    </>
  );
});

ActionHierarchy.displayName = 'ActionHierarchy';

export default ActionHierarchy;
